import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Container } from '../components/layout/Primitives';
import { Button } from '../components/ui/Button';
import { USE_API } from '../config/env';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { clearAuthError, registerThunk, selectAuthReady } from '../store/actions/authActions';

const PageWrap = styled.div`
  padding: clamp(2.5rem, 6vw, 4rem) 0 clamp(4rem, 10vw, 6rem);
  min-height: min(70vh, 720px);
`;

const Shell = styled.div`
  max-width: 28rem;
  margin: 0 auto;
`;

const Title = styled.h1`
  margin: 0 0 0.45rem;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(1.5rem, 2vw + 1rem, 2rem);
  font-weight: 300;
  text-align: center;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.foreground};
`;

const Lead = styled.p`
  margin: 0 0 2rem;
  text-align: center;
  font-size: ${({ theme }) => theme.type.bodySm};
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const Field = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.type.bodyXs};
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.foreground};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const AuthInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 0.75rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  font-size: ${({ theme }) => theme.type.bodySm};
  color: ${({ theme }) => theme.colors.foreground};
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.foreground};
  }
`;

const ErrorText = styled.p`
  margin: 0 0 1rem;
  font-size: ${({ theme }) => theme.type.bodySm};
  color: ${({ theme }) => theme.colors.terracottaDark};
`;

const Footer = styled.p`
  margin-top: 1.5rem;
  text-align: center;
  font-size: ${({ theme }) => theme.type.bodySm};
  color: ${({ theme }) => theme.colors.mutedForeground};

  a {
    text-decoration: underline;
    color: inherit;
    font-weight: 500;
  }
`;

function safePostAuthPath(redirect) {
  if (!redirect || typeof redirect !== 'string') return '/account';
  const d = decodeURIComponent(redirect.trim());
  return d.startsWith('/') && !d.startsWith('//') ? d : '/account';
}

export default function Register() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loggedIn = useAppSelector(selectAuthReady);
  const status = useAppSelector((s) => s.auth?.status ?? 'idle');
  const error = useAppSelector((s) => s.auth?.error);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');

  const redirectParam = searchParams.get('redirect');
  const postRegisterPath = useMemo(() => safePostAuthPath(redirectParam), [redirectParam]);

  const loginTo = useMemo(() => {
    if (!redirectParam) return '/login';
    return `/login?redirect=${encodeURIComponent(redirectParam)}`;
  }, [redirectParam]);

  useEffect(() => {
    dispatch(clearAuthError());
  }, [dispatch]);

  useEffect(() => {
    if (loggedIn) navigate(postRegisterPath, { replace: true });
  }, [loggedIn, navigate, postRegisterPath]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!USE_API) return;
    dispatch(
      registerThunk({
        email: email.trim(),
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim() || undefined,
      }),
    );
  };

  const busy = status === 'loading';

  return (
    <div className="animate-fade-in">
      <PageWrap>
        <Container>
          <Shell>
            <Title>{t('modamai.auth.registerTitle')}</Title>
            <Lead>{t('modamai.auth.registerLead')}</Lead>

            {!USE_API ? (
              <Lead style={{ marginBottom: 0 }}>{t('modamai.auth.registerApiOff')}</Lead>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                {error ? (
                  <ErrorText role="alert">
                    {typeof error === 'string' ? error : t('modamai.auth.registrationFailed')}
                  </ErrorText>
                ) : null}
                <Row>
                  <Field style={{ marginBottom: 0 }}>
                    <Label htmlFor="reg-first">{t('modamai.checkout.fieldFirstName')}</Label>
                    <AuthInput
                      id="reg-first"
                      autoComplete="given-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      disabled={busy}
                    />
                  </Field>
                  <Field style={{ marginBottom: 0 }}>
                    <Label htmlFor="reg-last">{t('modamai.checkout.fieldLastName')}</Label>
                    <AuthInput
                      id="reg-last"
                      autoComplete="family-name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      disabled={busy}
                    />
                  </Field>
                </Row>
                <Field>
                  <Label htmlFor="reg-email">{t('modamai.checkout.fieldEmail')}</Label>
                  <AuthInput
                    id="reg-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={busy}
                  />
                </Field>
                <Field>
                  <Label htmlFor="reg-phone">{t('modamai.auth.phoneOptional')}</Label>
                  <AuthInput
                    id="reg-phone"
                    type="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={busy}
                  />
                </Field>
                <Field>
                  <Label htmlFor="reg-password">{t('modamai.checkout.fieldPassword')}</Label>
                  <AuthInput
                    id="reg-password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    disabled={busy}
                  />
                </Field>
                <Button type="submit" $variant="luxe" $size="lg" style={{ width: '100%', marginTop: '0.25rem' }} disabled={busy}>
                  {busy ? t('modamai.auth.creating') : t('modamai.auth.createAccount')}
                </Button>
                <Footer>
                  {t('modamai.auth.alreadyRegistered')}{' '}
                  <Link to={loginTo}>{t('modamai.auth.signIn')}</Link>
                </Footer>
              </form>
            )}
          </Shell>
        </Container>
      </PageWrap>
    </div>
  );
}
